import {
  AuthorizedBaseAxios,
  BaseAxios,
  flatten,
  RedirectServerAxios,
} from 'src/config/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DispatchResultDTO, ProcessDTO } from 'src/common/models/processModels';
import { VerificationStatus } from 'src/common/models/verificationModels';
import {
  getRedirectUrlForProcess,
  getStatusOfVerificationProcess,
} from 'src/common/services/process';
import {
  RetryChoice,
  RetryMessageDTO,
} from 'src/pages/VerificationPage/verificationModels';
import { verificationQueryKeys } from 'src/common/verificationQueryKeys';
import { publicApiPath } from 'src/common/publicApiPath';
import {
  handleRedirectError,
  toErrorResponse,
} from 'src/common/services/errors';
import { pipe } from 'ramda';
import { extractValue, ValueWrapper } from 'src/utils/extractValue';
import {verificationIdParam} from "../../routes";

export const useStatusOfVerificationIntervalQuery = ({
  verificationId,
  enabled,
}: {
  verificationId?: string;
  enabled: boolean;
}) =>
  useQuery(
    verificationQueryKeys.status(verificationId),
    () =>
      verificationId
        ? getStatusOfVerificationProcess(verificationId)
        : undefined,
    {
      enabled: enabled && Boolean(verificationId),
      refetchInterval: (status) => {
        if (
          status &&
          [
            VerificationStatus.Expired,
            VerificationStatus.Done,
            VerificationStatus.Failed,
            VerificationStatus.InsufficientData,
          ].includes(status.verificationStatus)
        ) {
          return false;
        }
        return 5000;
      },
    }
  );

export const useRedirectUrlQuery = (verificationId?: string) =>
  useQuery(
    verificationQueryKeys.redirect(verificationId),
    () =>
      verificationId ? getRedirectUrlForProcess(verificationId) : undefined,
    {
      enabled: Boolean(verificationId),
      onError: handleRedirectError,
    }
  );

const getProcessRetryMessage = (verificationId: string) =>
  AuthorizedBaseAxios.get<RetryMessageDTO>(
    `${publicApiPath}/process/${verificationId}/retry/message`
  ).then(flatten);

export const useProcessRetryMessage = ({
  id,
  enabled,
}: {
  id?: string;
  enabled: boolean;
}) =>
  useQuery(
    verificationQueryKeys.retryMessage(id),
    () => (id ? getProcessRetryMessage(id) : undefined),
    {
      enabled,
    }
  );

interface RetryProcessDTO {
  verificationId: string;
  choice: RetryChoice;
}

const retryProcess = ({ verificationId, choice }: RetryProcessDTO) =>
  AuthorizedBaseAxios.post<ProcessDTO>(
    `${publicApiPath}/process/${verificationId}/retry`,
    { choice }
  ).then(flatten);

export const useProcessRetryMutation = () => useMutation(retryProcess);

interface SendVerificationReportDTO {
  verificationId: string;
  email: string;
}

const sendVerificationReport = ({
  verificationId,
  email,
}: SendVerificationReportDTO) =>
  AuthorizedBaseAxios.post<string>(
    `${publicApiPath}/${verificationId}/results`,
    {
      email,
    }
  ).then(flatten);

export const useSendVerificationReportMutation = () =>
  useMutation(sendVerificationReport);

const getCallback = (param: string, verificationId: string | null) =>
  BaseAxios.post<DispatchResultDTO>('/api/consents/callback', {
    authorizationParam: param,
    verificationId: verificationId,
  }).then(flatten);

export const useCallbackQuery = ({
  verificationId,
  param,
  enabled,
}: {
  verificationId: string | null;
  param: string | undefined;
  enabled: boolean;
}) =>
  useQuery<DispatchResultDTO | undefined>(
    verificationQueryKeys.callback(param),
    () => (param ? getCallback(param, verificationId) : undefined),
    {
      enabled: enabled && Boolean(param),
      //Bypass global error handling
      onError: (err) => {
        console.error(err);
        toErrorResponse(err);
      },
        onSuccess: (data) => {
          if (data?.verificationId) {
              localStorage.setItem(verificationIdParam, data.verificationId)
          }
        }
    }
  );

const getServerRedirect = (param: string) =>
  RedirectServerAxios.post<ValueWrapper<string>>(`${publicApiPath}/redirect`, {
    authorizationParam: param,
  }).then(pipe(flatten, extractValue));

export const useServerRedirectQuery = ({
  param,
  enabled,
}: {
  param: string | undefined;
  enabled: boolean;
}) =>
  useQuery(
    verificationQueryKeys.serverRedirect(param),
    () => (param ? getServerRedirect(param) : undefined),
    {
      enabled: enabled && Boolean(param),
    }
  );
